// filepath: apps/web/src/features/directory/organization-tree/OrganizationTreePage.tsx
//
// FR-UI-DIR-1 AC-3 — organization unit tree page.
// ISS-517 — inline Create-Child / Edit / Move / Archive actions and
// legal-entity-grouped headers.
//
// Fetches the flat list of organization units via
// useOrganizationUnits() (cache key includes tenantSlug per INV-1),
// groups them by `parent_organization_unit_id`, and recursively
// renders via OrganizationTreeNode.
//
// BA ruling note 18335 (D3): `legal_entity_id` is a business field,
// NOT an INV-1 security scope. The list OMITS the legal-entity filter
// — every tenant unit is shown — and each node is LABELLED with its
// legal-entity name resolved from `GET /api/directory/legal-entities`
// via useLegalEntityList(). The MR therefore carries NO INV-1 label.
//
// INV-2: the join is a render-time map lookup between two
// server-emitted payloads. No denormalised field is added to the
// OrganizationUnit record and nothing is synthesised.

import React, { useMemo } from "react";
import { useTranslation } from "@/i18n/useTranslation";
import { Button } from "@/design-system/Button";
import { navigate } from "@/features/cabinet/usePathname";
import { useLegalEntityList, useOrganizationUnitList } from "../queries";
import type { OrganizationUnit } from "../types";
import { OrganizationTreeNode } from "./OrganizationTreeNode";

export function OrganizationTreePage(): React.ReactElement {
  const { t } = useTranslation();
  const query = useOrganizationUnitList();
  const legalEntities = useLegalEntityList();

  const legalEntityNames = useMemo(() => {
    const map = new Map<string, string>();
    for (const le of legalEntities.data?.items ?? []) {
      map.set(le.id, le.name);
    }
    return map;
  }, [legalEntities.data]);

  switch (query.state) {
    case "loading":
      return (
        <p className="directory-org__loading" data-testid="org-loading">
          {t("directory.org.state.loading")}
        </p>
      );
    case "permission-denied":
      return (
        <p
          className="directory-org__permission-denied"
          role="alert"
          data-testid="org-permission-denied"
        >
          {t("directory.org.state.permission_denied")}
        </p>
      );
    case "validation-error":
      return (
        <p
          className="directory-org__validation-error"
          role="alert"
          data-testid="org-validation-error"
        >
          {query.error?.message ?? t("directory.org.state.validation_error")}
        </p>
      );
    case "fetch-failure":
      return (
        <p
          className="directory-org__fetch-failure"
          role="alert"
          data-testid="org-fetch-failure"
        >
          {t("directory.org.state.fetch_failure")}
        </p>
      );
    case "rate-limit":
      return (
        <p
          className="directory-org__rate-limit"
          role="alert"
          data-testid="org-rate-limit"
        >
          {t("directory.org.state.rate_limit")}
        </p>
      );
    case "success": {
      const data = query.data;
      if (data === null) {
        return (
          <p
            className="directory-org__fetch-failure"
            role="alert"
            data-testid="org-fetch-failure"
          >
            {t("directory.org.state.fetch_failure")}
          </p>
        );
      }
      const units = data.items ?? [];
      const unitNames = new Map<string, string>(
        units.map((u) => [u.id, u.name]),
      );
      const header = (
        <header className="directory-org__header">
          <h1>{t("directory.org.title", "Organization")}</h1>
          {/* design-adherence-ignore: <Button> test hook via wrapper. */}
          <span data-testid="org-create-unit">
            <Button
              type="button"
              variant="primary"
              onClick={() => navigate("/admin/organization-units/new")}
            >
              {t("directory.organization_units.action.create", "Create unit")}
            </Button>
          </span>
        </header>
      );
      if (units.length === 0) {
        return (
          <section className="directory-org" data-testid="org-page">
            {header}
            <p className="directory-org__empty" data-testid="org-empty">
              {t("directory.org.empty")}
            </p>
          </section>
        );
      }
      // Group children by parent_organization_unit_id. The "" bucket
      // holds the roots; a unit whose parent is absent from the
      // payload lands there too (orphan roots), so a partial server
      // response still renders a usable tree.
      const childrenByParent = new Map<string, OrganizationUnit[]>();
      for (const unit of units) {
        const key = unit.parent_organization_unit_id ?? "";
        const list = childrenByParent.get(key) ?? [];
        list.push(unit);
        childrenByParent.set(key, list);
      }
      const knownIds = new Set(units.map((u) => u.id));
      const roots = units.filter(
        (u) =>
          u.parent_organization_unit_id === null ||
          u.parent_organization_unit_id === undefined ||
          !knownIds.has(u.parent_organization_unit_id),
      );
      return (
        <section className="directory-org" data-testid="org-page">
          {header}
          <ul className="directory-org__tree" data-testid="org-tree">
            {roots.map((root) => (
              <OrganizationTreeNode
                key={root.id}
                unit={root}
                childrenByParent={childrenByParent}
                legalEntityNames={legalEntityNames}
                unitNames={unitNames}
              />
            ))}
          </ul>
        </section>
      );
    }
  }
}
